import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

export function useDustRanking(walletAddress, selectedCycle = null) {
  const [rankings, setRankings] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRankings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 선택된 사이클 또는 현재 월의 시작일과 종료일 계산
      let startOfMonth, endOfMonth;

      if (selectedCycle) {
        startOfMonth = new Date(selectedCycle.start);
        endOfMonth = new Date(selectedCycle.end);
      } else {
        const now = new Date();
        startOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
        endOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59, 999));
      }

      // 월별 trade_fee 합계를 buyer_wallet_address로 그룹화
      const { data, error: queryError } = await supabase
        .from('affiliate_histories')
        .select('buyer_wallet_address, trade_fee')
        .gte('trade_at', startOfMonth.toISOString())
        .lte('trade_at', endOfMonth.toISOString())
        .not('buyer_wallet_address', 'is', null);

      if (queryError) throw queryError;

      // buyer_wallet_address별로 trade_fee 합산
      const aggregated = {};
      (data || []).forEach((row) => {
        const addr = row.buyer_wallet_address;
        if (!addr) return;

        if (!aggregated[addr]) {
          aggregated[addr] = 0;
        }
        aggregated[addr] += parseFloat(row.trade_fee || 0);
      });

      // 배열로 변환하고 정렬
      let rankingArray = Object.entries(aggregated).map(([wallet_address, total_fee]) => ({
        wallet_address,
        total_fee,
      }));

      // trade_fee 총합 기준 내림차순 정렬
      rankingArray.sort((a, b) => b.total_fee - a.total_fee);

      // ssii_users 테이블에서 닉네임과 프로필 가져오기
      const walletAddresses = rankingArray.map(r => r.wallet_address);
      const { data: usersData } = await supabase
        .from('ssii_users')
        .select('wallet_address, nickname, profile')
        .in('wallet_address', walletAddresses);

      // 닉네임과 프로필 매핑
      const usersMap = {};
      (usersData || []).forEach(user => {
        usersMap[user.wallet_address] = {
          nickname: user.nickname,
          profile: user.profile,
        };
      });

      // 랭킹 데이터에 닉네임과 프로필 추가
      rankingArray = rankingArray.map(item => ({
        ...item,
        nickname: usersMap[item.wallet_address]?.nickname,
        profile: usersMap[item.wallet_address]?.profile,
      }));

      // Top 10 추출
      const top10 = rankingArray.slice(0, 10);
      setRankings(top10);

      // 내 랭킹 찾기
      if (walletAddress) {
        const myIndex = rankingArray.findIndex(
          (item) => item.wallet_address?.toLowerCase() === walletAddress.toLowerCase()
        );

        if (myIndex !== -1) {
          setMyRank({
            rank: myIndex + 1,
            ...rankingArray[myIndex],
          });
        } else {
          setMyRank(null);
        }
      }

    } catch (err) {
      console.error('Dust ranking error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [walletAddress, selectedCycle]);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  return { rankings, myRank, loading, error, refresh: fetchRankings };
}
